/**
 * Rulings and registry entries - the company's registry-file history.
 *
 * This is the tab public register services show as "Maarused", and its absence
 * was the most visible way our mirror looked thinner than theirs. One row is one
 * ruling: when it was made, what entry it produced, and whether it took effect.
 *
 * A long-lived company can carry a hundred of these, so the list opens on the
 * most recent handful and expands on request - the newest ruling is what a
 * salesperson is actually reading for.
 */
import { useState } from "react";
import { Gavel } from "lucide-react";
import { Badge, cn } from "@wa/ui";
import type { ArRuling } from "../../../lib/ariregister";
import { Card, fmtDate } from "./detailCard";

const COLLAPSED_ROWS = 8;

/** Ruling state codes seen in the source. "J" = in effect, "L" = ended/void. */
function stateVariant(state: string | null): "default" | "secondary" {
  return state === "J" ? "default" : "secondary";
}

export function RulingsCard({
  rulings,
  t,
}: {
  rulings: ArRuling[];
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (rulings.length === 0) return null;

  const shown = expanded ? rulings : rulings.slice(0, COLLAPSED_ROWS);
  const hidden = rulings.length - shown.length;

  return (
    <Card
      title={t("ariregister.detail.rulings.title")}
      icon={Gavel}
      aside={
        <span className="text-xs text-muted-foreground">
          {t("ariregister.detail.rulings.count", { n: rulings.length })}
        </span>
      }
    >
      {/* Wider than the sibling tables' 560px floor: a ruling type is a full
          sentence rather than a label, and at 560px it wrapped to four lines
          while every other cell in the row stayed on one - a row that was mostly
          empty band. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.rulings.date")}</th>
              <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.rulings.entryKind")}</th>
              <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.rulings.kind")}</th>
              <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.rulings.state")}</th>
              <th className="py-1.5 font-medium">{t("ariregister.detail.rulings.effective")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-b border-border/50 align-top">
                <td className="py-1.5 pr-3 tabular-nums whitespace-nowrap">
                  {fmtDate(r.rulingDate)}
                  <span className="ml-1.5 font-mono text-xs text-muted-foreground">{r.rulingNo}</span>
                </td>
                <td className="py-1.5 pr-3">{r.entryKindText || r.entryKind || "-"}</td>
                <td className="py-1.5 pr-3 text-muted-foreground">{r.kindText || r.kind || "-"}</td>
                <td className="py-1.5 pr-3">
                  {r.stateText || r.state ? (
                    <Badge variant={stateVariant(r.state)}>{r.stateText || r.state}</Badge>
                  ) : (
                    "-"
                  )}
                </td>
                <td className={cn("py-1.5 tabular-nums whitespace-nowrap", !r.effectiveAt && "text-muted-foreground")}>
                  {fmtDate(r.effectiveAt)}
                  {/* Only a defect ruling carries a deadline to cure it, and that
                      date is the one worth seeing - it means the entry is stuck. */}
                  {r.extraDeadline ? (
                    <span className="ml-1.5 text-xs text-amber-700 dark:text-amber-400">
                      {t("ariregister.detail.rulings.deadline", { date: fmtDate(r.extraDeadline) })}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hidden > 0 ? (
        <button type="button" className="mt-3 text-xs text-primary hover:underline" onClick={() => setExpanded(true)}>
          {t("ariregister.detail.rulings.showAll", { n: hidden })}
        </button>
      ) : null}
    </Card>
  );
}
